// src/component/PaymentPhaseAdmin2026.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  addDoc,
  getDoc,
  doc,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { auth, db } from "../api/firebase";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";

const TARGET_PHASE = 4; // ★ 2026 は paymentPhase = 4 を想定

const PaymentPhaseAdmin2026 = () => {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [phases, setPhases] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [newPhase, setNewPhase] = useState({
    label: "",
    priceTaxIncluded: "",
    priceTaxExcluded: "",
    priceIdTaxIncluded: "",
    priceIdTaxExcluded: "",
    start: "",
    end: "",
    phaseNumber: String(TARGET_PHASE),
    visible: true,
    isEarlyBird: false,
    adminOnly: false, // ★追加: 管理者のみ表示
  });

  // edit[id] = { start: "YYYY-MM-DDTHH:mm", end: "YYYY-MM-DDTHH:mm" }
  const [edit, setEdit] = useState({});

  // ─────────────────────────────────────────────
  // datetime-local 用: Firestore Timestamp/Date/string → "YYYY-MM-DDTHH:mm"
  // ─────────────────────────────────────────────
  const toDatetimeLocalValue = (value) => {
    if (!value) return "";

    // Firestore Timestamp
    if (value && typeof value.toDate === "function") {
      const d = value.toDate();
      if (isNaN(d.getTime())) return "";
      const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
      return local.toISOString().slice(0, 16);
    }

    // JS Date
    if (value instanceof Date) {
      if (isNaN(value.getTime())) return "";
      const local = new Date(value.getTime() - value.getTimezoneOffset() * 60000);
      return local.toISOString().slice(0, 16);
    }

    // string
    if (typeof value === "string") {
      // すでに "YYYY-MM-DDTHH:mm"
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return value;

      // その他は Date 変換を試す（移行期の保険）
      const d = new Date(value);
      if (isNaN(d.getTime())) return "";
      const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
      return local.toISOString().slice(0, 16);
    }

    return "";
  };

  // ─────────────────────────────────────────────
  // 初期ロード
  // ─────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      const user = auth.currentUser;
      if (!user) return navigate("/home");

      const userDoc = await getDoc(doc(db, "users", user.uid));
      const userData = userDoc.data();
      if (!userData?.isAdmin) return navigate("/home");
      setIsAdmin(true);

      const snap = await getDocs(collection(db, "paymentPhases2026"));
      const data = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((phase) => String(phase.phaseNumber) === String(TARGET_PHASE));

      setPhases(data);

      // ★ edit を初期化（これがないと入力欄が空になりがち）
      setEdit(() => {
        const init = {};
        data.forEach((p) => {
          init[p.id] = {
            start: toDatetimeLocalValue(p.start),
            end: toDatetimeLocalValue(p.end),
          };
        });
        return init;
      });

      setLoading(false);
    };

    fetchData();
  }, [navigate]);

  // ─────────────────────────────────────────────
  // 表示/非表示トグル
  // ─────────────────────────────────────────────
  const handleToggleVisible = async (phaseId, currentVisible) => {
    try {
      const newVisible = !currentVisible;
      await updateDoc(doc(db, "paymentPhases2026", phaseId), {
        visible: newVisible,
      });

      setPhases((prev) =>
        prev.map((phase) =>
          phase.id === phaseId ? { ...phase, visible: newVisible } : phase
        )
      );
    } catch (err) {
      console.error("表示状態の更新に失敗しました:", err);
    }
  };

  // ─────────────────────────────────────────────
  // 新規追加（Dialog）
  // ─────────────────────────────────────────────
  const handleAdd = async () => {
    try {
      const startDate = new Date(newPhase.start);
      if (isNaN(startDate.getTime())) return;

      const endDate = newPhase.end ? new Date(newPhase.end) : null;
      if (newPhase.end && isNaN(endDate.getTime())) return;

      await addDoc(collection(db, "paymentPhases2026"), {
        ...newPhase,
        isEarlyBird: !!newPhase.isEarlyBird, // ★追加
        phaseNumber: parseInt(newPhase.phaseNumber || TARGET_PHASE, 10),
        visible: newPhase.visible,
        start: Timestamp.fromDate(startDate),
        end: endDate ? Timestamp.fromDate(endDate) : null,
        createdAt: serverTimestamp(),
      });

      setNewPhase({
        label: "",
        priceTaxIncluded: "",
        priceTaxExcluded: "",
        priceIdTaxIncluded: "",
        priceIdTaxExcluded: "",
        start: "",
        end: "",
        phaseNumber: String(TARGET_PHASE),
        visible: true,
        isEarlyBird: false,
        adminOnly: false,
      });

      setDialogOpen(false);
      window.location.reload();
    } catch (err) {
      console.error("追加エラー:", err);
    }
  };

  // ─────────────────────────────────────────────
  // 入力中: edit に溜める（ここでFirestoreへは送らない）
  // ─────────────────────────────────────────────
  const setEditField = (id, key, value) => {
    setEdit((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [key]: value },
    }));
  };

  // ─────────────────────────────────────────────
  // 確定保存（onBlur）
  // ─────────────────────────────────────────────
  const commitStart = async (id) => {
    try {
      const v = edit[id]?.start || "";
      if (!v) return;

      const d = new Date(v);
      if (isNaN(d.getTime())) return; // 入力途中の値は保存しない

      await updateDoc(doc(db, "paymentPhases2026", id), {
        start: Timestamp.fromDate(d),
      });

      // phases も追随（見た目の整合性）
      setPhases((prev) =>
        prev.map((p) =>
          p.id === id ? { ...p, start: Timestamp.fromDate(d) } : p
        )
      );
    } catch (err) {
      console.error("開始日時の更新エラー:", err);
    }
  };

  const commitEnd = async (id) => {
    try {
      const v = edit[id]?.end || "";

      if (!v) {
        await updateDoc(doc(db, "paymentPhases2026", id), { end: null });
        setPhases((prev) =>
          prev.map((p) => (p.id === id ? { ...p, end: null } : p))
        );
        return;
      }

      const d = new Date(v);
      if (isNaN(d.getTime())) return;

      await updateDoc(doc(db, "paymentPhases2026", id), {
        end: Timestamp.fromDate(d),
      });

      setPhases((prev) =>
        prev.map((p) =>
          p.id === id ? { ...p, end: Timestamp.fromDate(d) } : p
        )
      );
    } catch (err) {
      console.error("終了日時の更新エラー:", err);
    }
  };

  // ─────────────────────────────────────────────
  // edit を phases 変化に追随（visible トグル等で phases 更新されるため）
  // ※ユーザーが編集中に edit を潰さないよう、未存在のidのみ補完する
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (!phases || phases.length === 0) return;

    setEdit((prev) => {
      const next = { ...prev };
      phases.forEach((p) => {
        if (!next[p.id]) {
          next[p.id] = {
            start: toDatetimeLocalValue(p.start),
            end: toDatetimeLocalValue(p.end),
          };
        }
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phases]);

  if (loading) return <CircularProgress sx={{ m: 5 }} />;
  if (!isAdmin) return null;

  return (
    <Container maxWidth="md">
      <Box display="flex" alignItems="center" justifyContent="space-between" mt={3}>
        <Typography variant="h5">MM2026 支払いフェーズ管理（第4期）</Typography>
        <IconButton onClick={() => setDialogOpen(true)}>
          <AddCircleIcon color="primary" fontSize="large" />
        </IconButton>
      </Box>

      <Box mt={4}>
        <Typography variant="subtitle1">第4期フェーズ</Typography>

        {phases.map((p) => (
          <Paper key={p.id} sx={{ p: 2, mt: 2 }}>
            <Typography fontWeight="bold">{p.label}</Typography>
            <Typography>第 {p.phaseNumber ?? "-"} 期</Typography>
            <Typography>税込価格: {p.priceTaxIncluded}</Typography>
            <Typography>税抜価格: {p.priceTaxExcluded}</Typography>
            <Typography>Price ID（国内）: {p.priceIdTaxIncluded}</Typography>
            <Typography>Price ID（海外）: {p.priceIdTaxExcluded}</Typography>


            <Box mt={2}>
              <TextField
                label="開始日時"
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={edit[p.id]?.start ?? ""}
                onChange={(e) => setEditField(p.id, "start", e.target.value)}
                onBlur={() => commitStart(p.id)}
              />

              <TextField
                label="終了日時"
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={edit[p.id]?.end ?? ""}
                onChange={(e) => setEditField(p.id, "end", e.target.value)}
                onBlur={() => commitEnd(p.id)}
                sx={{ mt: 2 }}
              />
            </Box>

            <Box mt={2} display="flex" alignItems="center">
              <Typography sx={{ mr: 2 }}>表示状態：</Typography>
              <Switch
                checked={!!p.visible}
                onChange={() => handleToggleVisible(p.id, !!p.visible)}
                color="primary"
              />
            </Box>
            <Box mt={2} display="flex" alignItems="center">
  <Typography sx={{ mr: 2 }}>早割：</Typography>
  <Switch
    checked={!!p.isEarlyBird}
    onChange={async () => {
      const next = !p.isEarlyBird;
      await updateDoc(doc(db, "paymentPhases2026", p.id), {
        isEarlyBird: next,
      });
      setPhases((prev) =>
        prev.map((phase) =>
          phase.id === p.id ? { ...phase, isEarlyBird: next } : phase
        )
      );
    }}
    color="primary"
  />
</Box>
            <Box mt={2} display="flex" alignItems="center">
              <Typography sx={{ mr: 2 }}>管理者のみ表示：</Typography>
              <Switch
                checked={!!p.adminOnly}
                onChange={async () => {
                  const next = !p.adminOnly;
                  await updateDoc(doc(db, "paymentPhases2026", p.id), {
                    adminOnly: next,
                  });
                  setPhases((prev) =>
                    prev.map((phase) =>
                      phase.id === p.id ? { ...phase, adminOnly: next } : phase
                    )
                  );
                }}
                color="warning"
              />
              <Typography variant="caption" sx={{ ml: 1.5, color: "rgba(0,0,0,0.55)" }}>
                ON: 一般ユーザーには非表示。管理者だけ閲覧可
              </Typography>
            </Box>

          </Paper>
        ))}
      </Box>

      {/* 新規フェーズ追加ダイアログ */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>新しいフェーズを追加（第4期）</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12}>
              <TextField
                label="表示ラベル（例: 早割 / 通常）"
                fullWidth
                value={newPhase.label}
                onChange={(e) => setNewPhase({ ...newPhase, label: e.target.value })}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="税込価格（国内）"
                fullWidth
                value={newPhase.priceTaxIncluded}
                onChange={(e) =>
                  setNewPhase({ ...newPhase, priceTaxIncluded: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="税抜価格（海外）"
                fullWidth
                value={newPhase.priceTaxExcluded}
                onChange={(e) =>
                  setNewPhase({ ...newPhase, priceTaxExcluded: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Stripe Price ID（国内）"
                fullWidth
                value={newPhase.priceIdTaxIncluded}
                onChange={(e) =>
                  setNewPhase({ ...newPhase, priceIdTaxIncluded: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Stripe Price ID（海外）"
                fullWidth
                value={newPhase.priceIdTaxExcluded}
                onChange={(e) =>
                  setNewPhase({ ...newPhase, priceIdTaxExcluded: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="開始日時"
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newPhase.start}
                onChange={(e) => setNewPhase({ ...newPhase, start: e.target.value })}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="終了日時（任意）"
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newPhase.end}
                onChange={(e) => setNewPhase({ ...newPhase, end: e.target.value })}
              />
            </Grid>

            {/* 期の選択は一応残すが、デフォルトは 4 */}
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="期（フェーズ番号）"
                value={newPhase.phaseNumber}
                onChange={(e) => setNewPhase({ ...newPhase, phaseNumber: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="">選択してください</option>
                {[4, 5, 6].map((num) => (
                  <option key={num} value={num}>
                    {num}
                  </option>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6} display="flex" alignItems="center">
              <Typography sx={{ mr: 2 }}>表示する</Typography>
              <Switch
                checked={!!newPhase.visible}
                onChange={(e) => setNewPhase({ ...newPhase, visible: e.target.checked })}
                color="primary"
              />
            </Grid>
            <Grid item xs={6} display="flex" alignItems="center">
  <Typography sx={{ mr: 2 }}>早割</Typography>
  <Switch
    checked={!!newPhase.isEarlyBird}
    onChange={(e) =>
      setNewPhase({ ...newPhase, isEarlyBird: e.target.checked })
    }
    color="primary"
  />
</Grid>
            <Grid item xs={12} display="flex" alignItems="center">
              <Typography sx={{ mr: 2 }}>管理者のみ表示</Typography>
              <Switch
                checked={!!newPhase.adminOnly}
                onChange={(e) =>
                  setNewPhase({ ...newPhase, adminOnly: e.target.checked })
                }
                color="warning"
              />
              <Typography variant="caption" sx={{ ml: 1.5, color: "rgba(0,0,0,0.55)" }}>
                ON: 一般ユーザーには非表示
              </Typography>
            </Grid>

          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleAdd} variant="contained">
            追加
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PaymentPhaseAdmin2026;
